import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import TierBadge from './TierBadge'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'

// Diagnostic panel — surfaces the full ModWash-NXT survey + scoring data
// for a single site. Designed to be a compact info-dense card that lives
// near the top of SiteDetail.
//
// Props:
//   det:       site_details row (with all diagnostic columns)
//   siteId:    UUID of the site (needed for the mutations)
//   onChange:  optional callback to re-fetch after a mutation
//
// The "Mark verified" action is visible to admins / executives /
// regional ops / remote techs — anyone qualified to confirm or amend
// the original survey diagnosis.
//
// The "→ Ticket" action on free-text findings is visible to admins
// and regional ops — the people who actually triage and assign work.
// Each finding-kind (gate, lpr, hardware, sensor) maps to one ticket;
// the action becomes "Ticketed" once a ticket exists for that kind.

const VERIFY_ROLES  = new Set(['admin','executive','regional_ops','remote_tech'])
const CONVERT_ROLES = new Set(['admin','regional_ops'])

const SCORE_BARS = [
  { key: 'lpr_score',       label: 'LPR',     max: 25, hint: 'License plate camera issues' },
  { key: 'camera_score',    label: 'Camera',  max: 25, hint: 'Camera placement penalty (4ft stand vs overhead)' },
  { key: 'revenue_score',   label: 'Revenue', max: 20, hint: 'Weekly net sales, scaled' },
  { key: 'gate_score',      label: 'Gate',    max: 15, hint: 'Reported gate hardware issues' },
  { key: 'lp_manual_score', label: 'Manual',  max: 15, hint: '% of plates entered manually at the kiosk' },
]

// Each finding-kind maps to a ticket title prefix and a default tier.
// Tiers default to the site's priority_tier (capitalized) when set,
// otherwise High — these findings always represent real equipment issues.
// The marker "[finding:<kind>]" goes in the ticket description so we can
// detect existing tickets for that kind on the same site (idempotency).
const FINDING_KINDS = {
  gate:     { titlePrefix: 'Gate issue',     type: 'Hardware' },
  lpr:      { titlePrefix: 'LPR issue',      type: 'Hardware' },
  hardware: { titlePrefix: 'Hardware needs', type: 'Hardware' },
  sensor:   { titlePrefix: 'Sensor issue',   type: 'Hardware' },
}

export default function DiagnosticPanel({ det, siteId, onChange }) {
  const { profile } = useAuth()
  const [verifySaving, setVerifySaving] = useState(false)
  const [verifyError,  setVerifyError]  = useState(null)
  const [findingTickets, setFindingTickets] = useState({}) // kind -> ticket row

  // Pull existing tickets that originated from a survey finding so we
  // can show "Ticketed" badges instead of duplicate-creating buttons.
  useEffect(() => {
    if (!siteId) return
    let cancelled = false
    ;(async () => {
      const { data } = await supabase
        .from('tickets')
        .select('id, ticket_number, title, status, description')
        .eq('site_id', siteId)
        .eq('source', 'survey')
      if (cancelled || !data) return
      const map = {}
      for (const t of data) {
        // We tagged the description on creation with [finding:<kind>]
        const m = (t.description || '').match(/\[finding:(\w+)\]/)
        if (m && FINDING_KINDS[m[1]] && !map[m[1]]) map[m[1]] = t
      }
      setFindingTickets(map)
    })()
    return () => { cancelled = true }
  }, [siteId, det])

  if (!det || det.priority_score == null) return null

  const totalScore = Number(det.priority_score)
  const verified = !!det.survey_verified
  const canVerify  = !!profile && VERIFY_ROLES.has(profile.role)  && siteId && !verified
  const canConvert = !!profile && CONVERT_ROLES.has(profile.role) && siteId

  async function handleVerify() {
    if (!canVerify || verifySaving) return
    setVerifySaving(true); setVerifyError(null)
    const { error } = await supabase
      .from('site_details')
      .update({
        survey_verified:    true,
        survey_verified_at: new Date().toISOString(),
        survey_verified_by: profile.id,
      })
      .eq('site_id', siteId)
    setVerifySaving(false)
    if (error) {
      setVerifyError(error.message)
      return
    }
    if (onChange) onChange()
  }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      {/* Header row: title + verified badge (with action button when applicable) */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, marginBottom: 10, flexWrap: 'wrap',
      }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Initial diagnostic
          {det.survey_completed_at && (
            <span style={{ marginLeft: 6 }}>
              · {new Date(det.survey_completed_at).toLocaleDateString()}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{
            fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 500,
            background: verified ? '#EAF3DE' : '#F1EFE8',
            color:      verified ? '#3B6D11' : '#5F5E5A',
          }}>
            {verified ? '✓ Verified' : 'Unverified'}
          </span>
          {canVerify && (
            <button
              className="btn btn-sm"
              disabled={verifySaving}
              onClick={handleVerify}
              title="Mark this site's diagnostic as verified by a qualified reviewer"
              style={{ fontSize: 11 }}>
              {verifySaving ? 'Saving…' : 'Mark verified'}
            </button>
          )}
        </div>
      </div>

      {/* Inline error if the verify call failed */}
      {verifyError && (
        <div style={{
          padding: '6px 10px', borderRadius: 6, marginBottom: 10,
          background: '#FCEBEB', color: '#A32D2D', fontSize: 12,
        }}>
          Couldn't save: {verifyError}
        </div>
      )}

      {/* Top row: tier + total score + key indicators */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 14 }}>
        <TierBadge tier={det.priority_tier} rank={det.priority_rank ?? det.rank} size="lg" />
        <div>
          <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1 }}>
            {totalScore.toFixed(1)}<span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 400 }}> / 100</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            Priority score
          </div>
        </div>
      </div>

      {/* Score breakdown bars */}
      <div style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
        {SCORE_BARS.map(b => {
          const v = det[b.key]
          if (v == null) return null
          const pct = Math.min(100, (Number(v) / b.max) * 100)
          const color = pct >= 80 ? '#A32D2D' : pct >= 50 ? '#854F0B' : '#5F5E5A'
          return (
            <div key={b.key} title={b.hint}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                <span style={{ color: 'var(--text-muted)' }}>{b.label}</span>
                <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                  {Number(v).toFixed(1)} / {b.max}
                </span>
              </div>
              <div style={{ height: 6, background: '#F1EFE8', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 0.3s ease' }} />
              </div>
            </div>
          )
        })}
      </div>

      {/* Hardware classification row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
        {det.camera_mount && (
          <ClassRow label="Cameras" value={cameraMountLabel(det.camera_mount)}
            warn={det.camera_mount === 'stand'}
            sub={det.camera_location && det.camera_location !== det.camera_mount ? det.camera_location : undefined} />
        )}
        {det.gate_type && <ClassRow label="Gate type" value={det.gate_type} />}
        {det.weekly_net_sales != null && (
          <ClassRow label="Weekly revenue" value={`$${Number(det.weekly_net_sales).toLocaleString(undefined, {maximumFractionDigits: 0})}`} />
        )}
        {det.cars_washed_weekly != null && (
          <ClassRow label="Cars / week" value={Number(det.cars_washed_weekly).toLocaleString()} />
        )}
        {det.manual_lp_pct != null && (
          <ClassRow label="Manual entry"
            value={`${(Number(det.manual_lp_pct) * 100).toFixed(1)}%`}
            warn={Number(det.manual_lp_pct) > 0.10} />
        )}
        {det.camera_update_cost != null && Number(det.camera_update_cost) > 0 && (
          <ClassRow label="Camera update est." value={`$${Number(det.camera_update_cost).toLocaleString()}`} />
        )}
        {det.lpr_stand_update_cost != null && Number(det.lpr_stand_update_cost) > 0 && (
          <ClassRow label="LPR relocation est." value={`$${Number(det.lpr_stand_update_cost).toLocaleString()}`} />
        )}
      </div>

      {/* Free-text findings — show only the ones that have content.
          When admin/regional_ops, each finding has a "→ Ticket" button
          that creates a pre-populated ticket linked back to this site.
          Once a ticket exists for a finding-kind, we show its ID instead. */}
      {(det.gate_issue_notes || det.lpr_issue_notes || det.hardware_replacement_notes || det.sensor_issue_notes) && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Reported by surveyor
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            <FindingRow kind="gate"     label="Gate issues"     text={det.gate_issue_notes}            siteId={siteId} det={det} canConvert={canConvert} existing={findingTickets.gate}     profile={profile} onChange={onChange} />
            <FindingRow kind="lpr"      label="LPR issues"      text={det.lpr_issue_notes}             siteId={siteId} det={det} canConvert={canConvert} existing={findingTickets.lpr}      profile={profile} onChange={onChange} />
            <FindingRow kind="hardware" label="Hardware needs"  text={det.hardware_replacement_notes}  siteId={siteId} det={det} canConvert={canConvert} existing={findingTickets.hardware} profile={profile} onChange={onChange} />
            <FindingRow kind="sensor"   label="Sensor notes"    text={det.sensor_issue_notes}          siteId={siteId} det={det} canConvert={canConvert} existing={findingTickets.sensor}   profile={profile} onChange={onChange} />
          </div>
        </div>
      )}

      {/* Surveyor attribution */}
      {det.surveyor_name && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Surveyed by {det.surveyor_name.trim()}
          {det.surveyor_role && ` (${det.surveyor_role.trim()})`}
        </div>
      )}

      {/* Verification footer — only when verified */}
      {verified && det.survey_verified_at && (
        <div style={{ marginTop: 4, fontSize: 11, color: '#3B6D11', fontStyle: 'italic' }}>
          ✓ Verified {new Date(det.survey_verified_at).toLocaleDateString()}
        </div>
      )}
    </div>
  )
}

function ClassRow({ label, value, sub, warn }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2, color: warn ? '#A32D2D' : 'var(--text)' }}>
        {warn && '⚠️ '}{value}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1, fontStyle: 'italic' }}>{sub}</div>
      )}
    </div>
  )
}

function FindingRow({ kind, label, text, siteId, det, canConvert, existing, profile, onChange }) {
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)

  if (!text || !String(text).trim()) return null
  const cleanText = String(text).trim()

  async function handleConvert() {
    if (!canConvert || creating) return
    setCreating(true); setError(null)
    const cfg = FINDING_KINDS[kind]
    // Use site's tier as ticket tier (capitalized for ticket schema)
    // Site detail tiers are lowercase, ticket tiers are capitalized — convert.
    const tierMap = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' }
    const tier = tierMap[det?.priority_tier] || 'High'
    const title = `${cfg.titlePrefix} — ${(det?.site_code || 'site').toUpperCase()}`
    // Embed a marker so we can detect existing tickets per finding-kind
    const description =
      `From initial NXT survey:\n\n${cleanText}\n\n` +
      (det?.surveyor_name ? `(Reported by ${det.surveyor_name.trim()}` + (det.surveyor_role ? `, ${det.surveyor_role.trim()})` : ')') + '\n\n' : '') +
      `[finding:${kind}]`
    const { data, error: err } = await supabase
      .from('tickets')
      .insert({
        site_id:     siteId,
        title, description,
        tier,
        type:        cfg.type,
        source:      'survey',
        status:      'open',
        created_by:  profile?.id || null,
      })
      .select('id, ticket_number')
      .single()
    setCreating(false)
    if (err) {
      setError(err.message)
      return
    }
    if (onChange) onChange()
    if (data?.id) navigate(`/tickets/${data.id}`)
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 }}>{label}</div>
        {existing ? (
          <button
            className="btn btn-sm"
            onClick={() => navigate(`/tickets/${existing.id}`)}
            title={`Linked ticket: ${existing.title} — status ${existing.status}`}
            style={{ fontSize: 10, padding: '2px 8px' }}>
            ✓ {existing.ticket_number}
          </button>
        ) : canConvert ? (
          <button
            className="btn btn-sm"
            disabled={creating}
            onClick={handleConvert}
            title="Create a ticket pre-filled with this finding"
            style={{ fontSize: 10, padding: '2px 8px' }}>
            {creating ? 'Creating…' : '→ Ticket'}
          </button>
        ) : null}
      </div>
      <div style={{ fontSize: 13, marginTop: 2, whiteSpace: 'pre-wrap' }}>{cleanText}</div>
      {error && (
        <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 2 }}>Couldn't create ticket: {error}</div>
      )}
    </div>
  )
}

function cameraMountLabel(mount) {
  switch (mount) {
    case 'stand':    return 'On 4ft stand'
    case 'overhead': return 'Overhead'
    case 'mixed':    return 'Mixed'
    case 'other':    return 'Other'
    default:         return mount
  }
}
